<template>
  <div class="form-wrapper">
    <form v-if="!submitted && !submitError"
          class="form"
          @submit.prevent="submitForm">
      <div class="form-group">
        <input
          v-model.trim="title"
          name="title"
          class="form-control"
          :placeholder="titlePlaceholder">
      </div>

      <div class="form-group">
        <input
          v-model.trim="firstName"
          name="firstName"
          class="form-control"
          :placeholder="firstNamePlaceholder">

        <div v-if="!$v.firstName.required && submitAttempted"
             class="validation-error">
          Please enter your first name
        </div>
      </div>

      <div class="form-group">
        <input
          v-model.trim="lastName"
          name="lastName"
          class="form-control"
          :placeholder="lastNamePlaceholder">

        <div v-if="!$v.lastName.required && submitAttempted"
             class="validation-error">
          Please enter your last name
        </div>
      </div>

      <div class="form-group">
        <input
          v-model.trim="email"
          name="email"
          type="email"
          class="form-control"
          :placeholder="emailPlaceholder">

        <div v-if="!$v.email.required && submitAttempted"
             class="validation-error">
          Please enter your email address
        </div>
      </div>

      <div class="form-group">
        <input
          v-model.trim="telephone"
          name="telephone"
          type="tel"
          class="form-control"
          :placeholder="telephonePlaceholder">
      </div>

      <div class="form-group">
        <input
          v-model.trim="schoolName"
          name="schoolName"
          class="form-control"
          :placeholder="schoolNamePlaceholder">
      </div>

      <div class="form-group">
        <input
          v-model.trim="postcode"
          name="postcode"
          class="form-control"
          :placeholder="postcodePlaceholder">
      </div>

      <div class="form-group">
        <textarea
          v-model="message"
          name="message"
          class="form-control message-input"
          :placeholder="messagePlaceholder"
          rows="5"></textarea>

        <div v-if="!$v.message.required && submitAttempted"
             class="validation-error">
          Please enter a message
        </div>
      </div>

      <div class="form-group">
        <button
          class="btn btn-accent-6 btn-rounded btn-submit"
          aria-label="Submit contact form"
          type="submit">
          <template v-if="!submitting">
            {{ submitButtonLabel }}
          </template>
          <template v-else>
            <font-awesome-icon icon="spinner"
                               spin></font-awesome-icon>
          </template>
        </button>
      </div>
    </form>
    <div v-if="submitted && !submitError"
         class="form-feedback">
      <h3 class="submit-success">
        Thank you!
      </h3>
      <p>Your message has been received.</p>
    </div>
    <div v-if="submitError"
         class="form-feedback">
      <h3 class="submit-error">
        Oh no!
      </h3>
      <p>Something went wrong whilst sending your message.</p>
      <p>
        Please email us at
        <a :href="'mailto:' + errorEmailAddress">{{ errorEmailAddress }}</a>.
      </p>
    </div>
  </div>
</template>

<script>
import Vue from "vue";
import { required } from "vuelidate/lib/validators";
import axios from "axios";
import qs from "qs";
import { VueReCaptcha } from "vue-recaptcha-v3";

// For more options see below
const siteKey = document.querySelector( "meta[name=\"siteKey\"]" ).getAttribute( "content" );
Vue.use( VueReCaptcha, { siteKey } );

export default {
  name: "ContactForm",
  props: {
    titlePlaceholder: {
      type: String,
      default: "Title"
    },
    firstNamePlaceholder: {
      type: String,
      default: "First name"
    },
    lastNamePlaceholder: {
      type: String,
      default: "Last name"
    },
    emailPlaceholder: {
      type: String,
      default: "Email"
    },
    telephonePlaceholder: {
      type: String,
      default: "Telephone"
    },
    schoolNamePlaceholder: {
      type: String,
      default: "School name"
    },
    postcodePlaceholder: {
      type: String,
      default: "Postcode"
    },
    messagePlaceholder: {
      type: String,
      default: "Message"
    },
    submitButtonLabel: {
      type: String,
      default: "Send"
    },
    errorEmailAddress: {
      type: String,
      default: "education@devoneducationservices.co.uk"
    },
    blogId: {
      type: String,
      default: null
    },
    marketingId: {
      type: String,
      default: null
    }
  },
  data () {
    return {
      title: null,
      firstName: null,
      lastName: null,
      email: null,
      telephone: null,
      schoolName: null,
      postcode: null,
      message: null,
      submitAttempted: false,
      submitError: false,
      submitting: false,
      submitted: false
    };
  },
  methods: {
    async getRecaptchaToken () {
      // (optional) Wait until recaptcha has been loaded.
      await this.$recaptchaLoaded();

      return this.$recaptcha( "submit" );
    },
    async submitForm () {
      this.submitAttempted = true;
      const token = await this.getRecaptchaToken();

      if ( !this.$v.$invalid ) {
        this.submitting = true;

        const url = "/umbraco/Surface/ContactSurface/Submit";
        const formData = {
          __RequestVerificationToken: this.$csrfToken,
          "g-recaptcha-response": token,
          title: this.title,
          firstName: this.firstName,
          lastName: this.lastName,
          email: this.email,
          telephone: this.telephone,
          schoolName: this.schoolName,
          postcode: this.postcode,
          message: this.message,
          blogId: this.blogId,
          marketingId: this.marketingId
        };

        axios
          .post( url, qs.stringify( formData ) )
          .then( response => {
            if ( response.data === true ) {
              this.title = "";
              this.firstName = "";
              this.lastName = "";
              this.email = "";
              this.telephone = "";
              this.schoolName = "";
              this.postcode = "";
              this.message = "";

              this.submitAttempted = false;
              this.submitted = true;
              this.submitting = false;
            } else {
              throw Error( "Bad response" );
            }
          } )
          .catch( error => {
            // Catch it so we can show a pretty error,
            // then throw it again for Sentry to collect.
            this.submitError = true;
            throw new Error( error );
          } );
      }
    }
  },
  validations: {
    firstName: {
      required
    },
    lastName: {
      required
    },
    email: {
      required
    },
    message: {
      required
    }
  }
};
</script>

<style lang="less" scoped>
.form-wrapper {
  max-width: 400px;

  input,
  textarea {
    border: 2px solid #78b7bc;
  }

  @media (max-width: 991px) {
    max-width: none;
    margin-left: -15px;
    margin-right: -15px;
  }
}

.btn-submit {
  min-width: 120px;
}
</style>
